import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { MiniTimelineStore } from './MiniTimelineStore';
import { MiniAudioPlayer } from './MiniAudioPlayer';

interface MiniTimelineViewProps {
  timelineStore: MiniTimelineStore;
  audioPlayer: MiniAudioPlayer;
}

/**
 * Component for visualizing and editing transcript element timing
 */
export const MiniTimelineView: React.FC<MiniTimelineViewProps> = observer(({
  timelineStore,
  audioPlayer
}) => {
  // Refs for DOM elements
  const waveformCanvasRef = useRef<HTMLCanvasElement>(null);
  const timelineCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Dragging state
  const [draggingElement, setDraggingElement] = useState<{
    elementId: string;
    segmentId: string;
    paragraphId: string;
    handle: 'start' | 'end';
    initialTime: number;
  } | null>(null);
  
  // Formatting time for display
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);
  
  // Convert time to horizontal position
  const timeToPosition = useCallback((time: number): number => {
    if (!containerRef.current) return 0;
    
    const { width } = containerRef.current.getBoundingClientRect();
    const [startTime, endTime] = timelineStore.visibleTimeRange;
    const timeRange = endTime - startTime;
    
    return ((time - startTime) / timeRange) * width;
  }, [timelineStore.visibleTimeRange]);
  
  // Convert horizontal position to time
  const positionToTime = useCallback((position: number): number => {
    if (!containerRef.current) return 0;
    
    const { width } = containerRef.current.getBoundingClientRect();
    const [startTime, endTime] = timelineStore.visibleTimeRange;
    const timeRange = endTime - startTime;
    
    return startTime + (position / width) * timeRange;
  }, [timelineStore.visibleTimeRange]);
  
  // Draw the time ruler
  const drawTimeRuler = useCallback(() => {
    const canvas = timelineCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set drawing styles
    ctx.fillStyle = '#a9a9a9';
    ctx.font = '10px monospace';
    ctx.textBaseline = 'top';
    
    // Calculate time markers
    const [startTime, endTime] = timelineStore.visibleTimeRange;
    const timeRange = endTime - startTime;
    
    // Determine appropriate time marker interval
    let interval: number;
    if (timeRange <= 5) interval = 0.5;      // 0.5 seconds
    else if (timeRange <= 15) interval = 1;  // 1 second
    else if (timeRange <= 60) interval = 5;  // 5 seconds
    else interval = 15;                      // 15 seconds
    
    // Draw time markers
    const firstMarker = Math.ceil(startTime / interval) * interval;
    
    for (let time = firstMarker; time <= endTime; time += interval) {
      const x = timeToPosition(time);
      
      // Major tick (labeled)
      if (time % (interval * 2) === 0) {
        ctx.fillRect(x, 0, 1, 10);
        ctx.fillText(formatTime(time), x + 2, 0);
      }
      // Minor tick
      else {
        ctx.fillRect(x, 0, 1, 5);
      }
    }
    
    // Draw current time playhead
    const playheadX = timeToPosition(timelineStore.currentTime);
    ctx.fillStyle = '#f2d093';
    ctx.fillRect(playheadX - 1, 0, 2, canvas.height);
    
  }, [timelineStore.visibleTimeRange, timelineStore.currentTime, timeToPosition, formatTime]);
  
  // Draw the waveform
  const drawWaveform = useCallback(() => {
    const canvas = waveformCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // If we have waveform data, draw it
    let waveformData = timelineStore.waveformData ? timelineStore.waveformData.toJSON() : null;
    if (waveformData) {
      // Ensure we have valid data to work with
      const waveformDataArray = waveformData.data || [];
      // Calculate visible sample range
      const [startTime, endTime] = timelineStore.visibleTimeRange;
      const totalDuration = timelineStore.totalTimeRange[1] - timelineStore.totalTimeRange[0];
      
      if (totalDuration > 0) {
        // Calculate sample indexes
        const totalSamples = waveformDataArray.length;
        const samplesPerSecond = totalSamples / totalDuration;

        // Calculate start and end indices based on the visible time range
        // Adjust to use the appropriate offset relative to the total range
        const startIndex = Math.max(0, Math.floor((startTime - timelineStore.totalTimeRange[0]) * samplesPerSecond));
        const endIndex = Math.min(totalSamples, Math.ceil((endTime - timelineStore.totalTimeRange[0]) * samplesPerSecond));
        
        // Calculate display parameters
        const visibleSamples = endIndex - startIndex;
        const pixelsPerSample = canvas.width / visibleSamples;
        
        // Determine appropriate downsampling factor
        let downsample = 1;
        if (pixelsPerSample < 0.1) downsample = Math.ceil(1 / pixelsPerSample);
        
        // Set up gradient for waveform
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#8ab5bf');   // Top color
        gradient.addColorStop(0.5, '#6c9ba5'); // Middle color
        gradient.addColorStop(1, '#4a7a85');   // Bottom color
        
        // Find selected elements time range
        const selectedElements = timelineStore.elements.filter(e => e.isEditable);
        let selectionStart = Number.MAX_VALUE;
        let selectionEnd = 0;
        
        if (selectedElements.length > 0) {
          selectedElements.forEach(el => {
            if (el.element.type === 'text') {
              const startTime = el.adjustedStartTime ?? el.originalStartTime;
              const endTime = el.adjustedEndTime ?? el.originalEndTime;
              
              if (startTime !== undefined && startTime < selectionStart) {
                selectionStart = startTime;
              }
              
              if (endTime !== undefined && endTime > selectionEnd) {
                selectionEnd = endTime;
              }
            }
          });
        }
        
        // Draw waveform samples that correspond to our visible time range
        for (let i = startIndex; i < endIndex; i += downsample) {
          if (i >= 0 && i < waveformDataArray.length) {
            const value = waveformDataArray[i] / 255; // Normalize to 0-1
            const height = value * canvas.height * 0.8;
            
            // Position horizontally based on position within visible range
            const x = ((i - startIndex) / Math.max(1, visibleSamples)) * canvas.width;
            const y = (canvas.height - height) / 2;
            
            const width = Math.max(1, pixelsPerSample * downsample);
            
            // Calculate current time for this sample
            const sampleTime = timelineStore.totalTimeRange[0] + (i / samplesPerSecond);
            
            // Highlight selection area
            if (sampleTime >= selectionStart && sampleTime <= selectionEnd) {
              ctx.fillStyle = '#d1a669'; // Highlight color for selected area
            } else {
              // Use intensity-based color for regular waveform
              const intensity = Math.min(1, value * 1.5); // Boost low values slightly
              if (intensity > 0.7) {
                ctx.fillStyle = '#f2d093'; // Higher amplitude
              } else if (intensity > 0.4) {
                ctx.fillStyle = '#6c9ba5'; // Medium amplitude
              } else {
                ctx.fillStyle = '#4a7a85'; // Lower amplitude
              }
            }
            
            // Create a slightly different visual for current time
            const currentTimeWindow = 0.05; // 50ms window around current time
            if (Math.abs(sampleTime - timelineStore.currentTime) < currentTimeWindow) {
              // Make the sample at current time brighter
              ctx.fillStyle = '#ffffff';
            }
            
            ctx.fillRect(x, y, width, height);
          }
        }
      }
    } else {
      // Draw placeholder waveform if no data
      ctx.fillStyle = '#3a535a';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No waveform data available', canvas.width / 2, canvas.height / 2);
    }
    
    // Draw current time playhead
    const playheadX = timeToPosition(timelineStore.currentTime);
    ctx.fillStyle = '#f2d093';
    ctx.fillRect(playheadX - 1, 0, 2, canvas.height);
    
  }, [timelineStore.waveformData, timelineStore.visibleTimeRange, timelineStore.totalTimeRange, timelineStore.currentTime, timelineStore.elements, timeToPosition]);
  
  // Draw the timeline elements
  const renderElements = useCallback(() => {
    // First, get all visible elements
    const visibleElements = timelineStore.elements
      // Filter to only show elements in the visible range
      .filter(element => {
        if (element.element.type !== 'text') return false;
        
        const startTime = element.adjustedStartTime ?? element.originalStartTime;
        const endTime = element.adjustedEndTime ?? element.originalEndTime;
        
        if (startTime === undefined || endTime === undefined) return false;
        
        // Element is visible if any part of it is in the visible range
        return (
          (startTime >= timelineStore.visibleTimeRange[0] && startTime <= timelineStore.visibleTimeRange[1]) ||
          (endTime >= timelineStore.visibleTimeRange[0] && endTime <= timelineStore.visibleTimeRange[1]) ||
          (startTime <= timelineStore.visibleTimeRange[0] && endTime >= timelineStore.visibleTimeRange[1])
        );
      })
      // Sort by start time
      .sort((a, b) => {
        const aStart = a.adjustedStartTime ?? a.originalStartTime ?? 0;
        const bStart = b.adjustedStartTime ?? b.originalStartTime ?? 0;
        return aStart - bStart;
      });
    
    // Group visible elements by vertical position to avoid overlapping
    const verticalPositions: {[key: number]: Array<{
      element: typeof visibleElements[0];
      startPos: number;
      endPos: number;
      width: number;
      isModified: boolean;
    }>} = {};
    
    // Process each element and assign a vertical position
    visibleElements.forEach(element => {
      const startTime = element.adjustedStartTime ?? element.originalStartTime;
      const endTime = element.adjustedEndTime ?? element.originalEndTime;
      
      if (startTime === undefined || endTime === undefined) return null;
      
      const startPos = timeToPosition(startTime);
      const endPos = timeToPosition(endTime);
      const width = Math.max(5, endPos - startPos);
      
      const isModified = (
        element.adjustedStartTime !== undefined && 
        element.adjustedStartTime !== element.originalStartTime
      ) || (
        element.adjustedEndTime !== undefined && 
        element.adjustedEndTime !== element.originalEndTime
      );
      
      // Find a vertical position where this element doesn't overlap with others
      let verticalPos = 0;
      let foundPosition = false;
      
      // Buffer for visual spacing when elements don't actually overlap in time
      // This is smaller than the actual timing overlap threshold
      const visualBuffer = 10; // 10px visual buffer between non-overlapping elements
      
      while (!foundPosition) {
        const elementsAtPos = verticalPositions[verticalPos] || [];
        
        // Determine actual timing overlap
        const realTimeOverlap = elementsAtPos.some(existing => {
          // Get actual start and end times
          const existingElement = existing.element;
          const existingStartTime = existingElement.adjustedStartTime ?? existingElement.originalStartTime ?? 0;
          const existingEndTime = existingElement.adjustedEndTime ?? existingElement.originalEndTime ?? 0;
          const currentStartTime = element.adjustedStartTime ?? element.originalStartTime ?? 0;
          const currentEndTime = element.adjustedEndTime ?? element.originalEndTime ?? 0;
          
          // Check for actual timing overlap (this is the crucial check)
          return (
            (currentStartTime < existingEndTime && currentEndTime > existingStartTime)
          );
        });
        
        // Check for visual overlap - only used if no real time overlap
        const visualOverlap = elementsAtPos.some(existing => {
          // Only apply visual buffer if the elements don't actually overlap in time
          return (startPos < existing.endPos + visualBuffer && endPos > existing.startPos - visualBuffer);
        });
        
        // If we have real time overlap, always stack
        // If no real time overlap but visual overlap, only stack if we're too close visually
        const needsStacking = realTimeOverlap || visualOverlap;
        
        if (!needsStacking) {
          // Found a position where this element doesn't overlap
          foundPosition = true;
          
          // Initialize the array if it doesn't exist
          verticalPositions[verticalPos] = verticalPositions[verticalPos] || [];
          
          // Add the element to this position
          verticalPositions[verticalPos].push({
            element,
            startPos,
            endPos,
            width,
            isModified
          });
        } else {
          // Try the next vertical position
          verticalPos++;
        }
      }
    });
    
    // Now render elements at their assigned vertical positions
    return Object.entries(verticalPositions).flatMap(([verticalPos, elements]) => {
      return elements.map(({ element, startPos, width, isModified }) => {
        // Calculate top position based on vertical position (10px per row, starting at 10px)
        const topPosition = 10 + (parseInt(verticalPos) * 32);
        
        const startTime = element.adjustedStartTime ?? element.originalStartTime;
        const endTime = element.adjustedEndTime ?? element.originalEndTime;
        
        if (startTime === undefined || endTime === undefined) return null;
        
        return (
          <TimelineElement 
            key={`${element.segmentId}-${element.paragraphId}-${element.elementId}`}
            style={{ left: startPos, width, top: topPosition }}
            isEditable={element.isEditable}
            isModified={isModified}
            title={element.element.value}
          >
            {element.isEditable && (
              <>
                <StartHandle
                  onMouseUp={(e) => {
                    console.log('StartHandle onMouseUp', e)
                  }}
                  onClick={(e) => {
                    console.log('StartHandle onClick', e)
                  }}
                  onMouseDown={(e) => {
                    console.log('StartHandle onMouseDown', e)
                    e.preventDefault();
                    e.stopPropagation();
                    if (!element.isEditable) return;

                    setDraggingElement({
                      elementId: element.elementId,
                      segmentId: element.segmentId,
                      paragraphId: element.paragraphId,
                      handle: 'start',
                      initialTime: startTime
                    });
                  }}
                />
                <EndHandle
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!element.isEditable) return;

                    setDraggingElement({
                      elementId: element.elementId,
                      segmentId: element.segmentId,
                      paragraphId: element.paragraphId,
                      handle: 'end',
                      initialTime: endTime
                    });
                  }}
                />
              </>
            )}
            {element.element.value}
          </TimelineElement>
        );
      });
    });
  }, [timelineStore.elements, timelineStore.visibleTimeRange, timeToPosition]);
  
  // Render silence indicators
  const renderSilences = useCallback(() => {
    return timelineStore.silences
      // Filter to only show silences in the visible range
      .filter(silence => (
        (silence.startTime >= timelineStore.visibleTimeRange[0] && silence.startTime <= timelineStore.visibleTimeRange[1]) ||
        (silence.endTime >= timelineStore.visibleTimeRange[0] && silence.endTime <= timelineStore.visibleTimeRange[1]) ||
        (silence.startTime <= timelineStore.visibleTimeRange[0] && silence.endTime >= timelineStore.visibleTimeRange[1])
      ))
      // Map to silence indicator components
      .map((silence, index) => {
        const startPos = timeToPosition(silence.startTime);
        const endPos = timeToPosition(silence.endTime);
        const width = Math.max(5, endPos - startPos);
        
        // Calculate number of dots based on duration
        const dotCount = Math.min(5, Math.max(1, Math.floor(silence.duration)));
        
        return (
          <SilenceIndicator 
            key={`silence-${index}`}
            style={{ left: startPos, width }}
            title={`Silence: ${silence.duration.toFixed(1)}s`}
          >
            {Array.from({ length: dotCount }).map((_, i) => (
              <SilenceDot key={i} />
            ))}
          </SilenceIndicator>
        );
      });
  }, [timelineStore.silences, timelineStore.visibleTimeRange, timeToPosition]);
  
  // State for showing time delta during drag
  const [timeDelta, setTimeDelta] = useState<{ value: number, x: number, y: number } | null>(null);
  
  // Handle mouse interactions for dragging and seeking
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLElement> | MouseEvent) => {
    if (!containerRef.current || !draggingElement) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ('clientX' in e) ? e.clientX - rect.left : 0;
    let time = positionToTime(x);
    
    // Detect if Shift key is pressed for fine-grained control
    const isShiftKey = e.shiftKey;
    
    // Apply fine-grained control (move in 0.01s increments) when shift is pressed
    if (isShiftKey) {
      // Round to nearest 0.01s (10ms) when Shift is pressed 
      time = Math.round(time * 100) / 100;
    } else {
      // Round to nearest 0.05s (50ms) for normal movement
      time = Math.round(time * 20) / 20;
    }
    
    // Calculate time delta
    const delta = time - draggingElement.initialTime;
    
    // Update time delta display
    setTimeDelta({
      value: delta,
      x: e.clientX,
      y: e.clientY - 30 // Position above cursor
    });
    
    // Update element timing based on which handle is being dragged
    if (draggingElement.handle === 'start') {
      // Find the element being adjusted
      const element = timelineStore.elements.find(
        el => el.elementId === draggingElement.elementId && 
              el.segmentId === draggingElement.segmentId && 
              el.paragraphId === draggingElement.paragraphId
      );
      
      if (element) {
        // Ensure start time doesn't exceed end time
        const endTime = element.adjustedEndTime ?? element.originalEndTime;
        if (endTime !== undefined && time >= endTime) {
          time = endTime - 0.01; // Ensure a minimum gap of 0.01s
        }
      }
      
      timelineStore.adjustElementStart(
        draggingElement.elementId,
        draggingElement.segmentId,
        draggingElement.paragraphId,
        time
      );
    } else {
      // Find the element being adjusted
      const element = timelineStore.elements.find(
        el => el.elementId === draggingElement.elementId && 
              el.segmentId === draggingElement.segmentId && 
              el.paragraphId === draggingElement.paragraphId
      );
      
      if (element) {
        // Ensure end time doesn't go below start time
        const startTime = element.adjustedStartTime ?? element.originalStartTime;
        if (startTime !== undefined && time <= startTime) {
          time = startTime + 0.01; // Ensure a minimum gap of 0.01s
        }
      }
      
      timelineStore.adjustElementEnd(
        draggingElement.elementId,
        draggingElement.segmentId,
        draggingElement.paragraphId,
        time
      );
    }
  }, [containerRef, draggingElement, positionToTime, timelineStore]);
  
  // Clear time delta when done dragging
  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLElement> | MouseEvent) => {
    setDraggingElement(null);
    setTimeDelta(null);
  }, []);
  
  
  // Handle clicks on the waveform or timeline for seeking
  const handleTimelineClick = useCallback((e: React.MouseEvent) => {
    console.log('handleTimelineClick', e)
    if (!containerRef.current) return;
    
    // If we're dragging, don't handle click
    if (draggingElement) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const time = positionToTime(x);
    
    // Seek to the clicked time
    audioPlayer.seekTo(time);
  }, [containerRef, positionToTime, audioPlayer, draggingElement]);
  
  // Handle mouse wheel for zooming
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!containerRef.current) return;
    
    // Prevent default scrolling
    e.preventDefault();
    
    // Get mouse position
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    
    // Convert to time
    const mouseTime = positionToTime(mouseX);
    
    // Calculate new visible range based on wheel delta
    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
    const currentRange = timelineStore.visibleTimeRange[1] - timelineStore.visibleTimeRange[0];
    const newRange = currentRange * zoomFactor;
    
    // Calculate new start and end times, keeping mouse position fixed
    const mouseRatio = (mouseTime - timelineStore.visibleTimeRange[0]) / currentRange;
    const newStart = mouseTime - (mouseRatio * newRange);
    const newEnd = newStart + newRange;
    
    // Update the visible time range
    timelineStore.setVisibleTimeRange(newStart, newEnd);
  }, [containerRef, positionToTime, timelineStore]);
  
  // Handle keyboard events for navigation and editing
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Skip if we're in an input field
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }
    
    // Handle keyboard shortcuts
    switch (e.key) {
      case 'ArrowLeft':
        // Skip back 0.1s (or 0.01s with shift)
        const backwardSkip = e.shiftKey ? 0.01 : 0.1;
        audioPlayer.seekTo(Math.max(timelineStore.totalTimeRange[0], timelineStore.currentTime - backwardSkip));
        e.preventDefault();
        break;
        
      case 'ArrowRight':
        // Skip forward 0.1s (or 0.01s with shift)
        const forwardSkip = e.shiftKey ? 0.01 : 0.1;
        audioPlayer.seekTo(Math.min(timelineStore.totalTimeRange[1], timelineStore.currentTime + forwardSkip));
        e.preventDefault();
        break;
        
      case ' ':
        // Space = play/pause
        if (audioPlayer.isPlaying) {
          audioPlayer.pause();
        } else {
          audioPlayer.play();
        }
        e.preventDefault();
        break;
        
      case '+':
      case '=':
        // Zoom in
        timelineStore.zoomIn();
        e.preventDefault();
        break;
        
      case '-':
        // Zoom out
        timelineStore.zoomOut();
        e.preventDefault();
        break;
        
      case '0':
        // Reset zoom
        timelineStore.fitToTimeline();
        e.preventDefault();
        break;
        
      case 'l':
        // Toggle loop
        // This would need to be implemented in the parent component
        e.preventDefault();
        break;
    }
  }, [audioPlayer, timelineStore]);
  
  // Set up mouse event listeners on the container element
  useEffect(() => {
    const container = containerRef.current;
    if (container && draggingElement) {
      // Add listeners directly to the container
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [draggingElement, handleMouseMove, handleMouseUp, containerRef]);
  
  // Set up wheel event listener for zooming
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [containerRef, handleWheel]);
  
  // Set up keyboard event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // Auto-scroll to keep playhead in view
  useEffect(() => {
    if (!timelineStore.isPlaying) return;
    
    const playheadPosition = timeToPosition(timelineStore.currentTime);
    const containerWidth = containerRef.current?.clientWidth || 0;
    
    // If playhead is approaching the right edge (within 15% of width)
    if (playheadPosition > containerWidth * 0.85) {
      // Calculate new visible range
      const currentRange = timelineStore.visibleTimeRange[1] - timelineStore.visibleTimeRange[0];
      const newStart = timelineStore.currentTime - (currentRange * 0.2); // 20% from left
      const newEnd = newStart + currentRange;
      
      // Adjust the visible time range
      timelineStore.setVisibleTimeRange(newStart, newEnd);
    }
  }, [timelineStore.currentTime, timelineStore.isPlaying, timeToPosition, containerRef, timelineStore]);
  
  // Re-render canvases when needed
  useEffect(() => {
    const updateCanvasSize = () => {
      if (waveformCanvasRef.current && containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        waveformCanvasRef.current.width = width;
        waveformCanvasRef.current.height = 100;
        
        // Scale the canvas context for high-DPI displays
        const ctx = waveformCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.scale(1, 1);
        }
      }
      
      if (timelineCanvasRef.current && containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        timelineCanvasRef.current.width = width;
        timelineCanvasRef.current.height = 20;
        
        // Scale the canvas context for high-DPI displays
        const ctx = timelineCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.scale(1, 1);
        }
      }
      
      drawTimeRuler();
      drawWaveform();
    };
    
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [drawTimeRuler, drawWaveform]);
  
  // Update canvases when store data changes
  useEffect(() => {
    drawTimeRuler();
    drawWaveform();
  }, [
    timelineStore.visibleTimeRange, 
    timelineStore.currentTime, 
    timelineStore.waveformData,
    drawTimeRuler,
    drawWaveform
  ]);
  
  return (
    <TimelineContainer ref={containerRef} onClick={handleTimelineClick}>
      <TimeRuler>
        <TimelineCanvas ref={timelineCanvasRef} />
      </TimeRuler>
      
      <WaveformContainer>
        <WaveformCanvas ref={waveformCanvasRef} />
      </WaveformContainer>
      
      <ElementsTrack>
        <Playhead style={{ left: timeToPosition(timelineStore.currentTime) }} />
        {renderSilences()}
        {renderElements()}
      </ElementsTrack>
      
      {/* Time delta indicator */}
      {timeDelta && (
        <TimeDeltaIndicator
          style={{
            left: timeDelta.x,
            top: timeDelta.y,
            backgroundColor: timeDelta.value > 0 ? 'rgba(108, 155, 165, 0.9)' : 'rgba(164, 108, 133, 0.9)'
          }}
        >
          {timeDelta.value > 0 ? '+' : ''}{timeDelta.value.toFixed(3)}s
        </TimeDeltaIndicator>
      )}
    </TimelineContainer>
  );
});

// Styled components
const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 250px;
  background-color: #222233;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
`;

const TimeRuler = styled.div`
  height: 20px;
  width: 100%;
  background-color: #1a1a2a;
  position: relative;
`;

const TimelineCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const WaveformContainer = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: #1e1e2c;
`;

const WaveformCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ElementsTrack = styled.div`
  width: 100%;
  height: 150px;
  background-color: #2a2a3c;
  position: relative;
  overflow-y: auto; /* Allow scrolling if many element rows */
  overflow-x: hidden;
`;

const Playhead = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 2px;
  background-color: #f2d093;
  z-index: 10;
  pointer-events: none;
`;

const TimelineElement = styled.div<{ isEditable: boolean; isModified: boolean }>`
  position: absolute;
  height: 30px;
  top: 10px;
  background-color: ${props => 
    props.isModified 
      ? props.isEditable ? 'rgba(242, 208, 147, 0.7)' : 'rgba(106, 90, 70, 0.6)'
      : props.isEditable ? 'rgba(108, 155, 165, 0.7)' : 'rgba(70, 90, 95, 0.6)'
  };
  border: 1px solid ${props => 
    props.isModified
      ? props.isEditable ? '#f2d093' : '#a5906c'
      : props.isEditable ? '#6c9ba5' : '#3a535a'
  };
  border-radius: 3px;
  padding: 4px 10px;
  font-size: 12px;
  color: ${props => props.isEditable ? '#ffffff' : '#a9a9a9'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
  ${props => !props.isEditable && 'pointer-events: none;'}
  z-index: 5; /* Add z-index to prevent elements from overlapping */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: box-shadow 0.2s ease;
  
  &:hover {
    box-shadow: ${props => props.isEditable ? '0 2px 6px rgba(0, 0, 0, 0.3)' : 'none'};
  }
`;

const HandleBase = styled.div`
  position: absolute;
  width: 6px;
  height: 100%;
  top: 0;
  background-color: rgba(0, 0, 0, 0.3);
  cursor: col-resize;
  z-index: 6;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
    width: 8px; /* Slightly wider on hover for easier grabbing */
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 2px;
    height: 24px;
    top: 3px;
    left: 2px;
    background-color: rgba(255, 255, 255, 0.4);
    border-radius: 1px;
  }
`;

const StartHandle = styled(HandleBase)`
  left: 0;
`;

const EndHandle = styled(HandleBase)`
  right: 0;
`;

const SilenceIndicator = styled.div`
  position: absolute;
  height: 20px;
  top: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const SilenceDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #a9a9a9;
  margin: 0 3px;
`;

const TimeDeltaIndicator = styled.div`
  position: fixed;
  transform: translate(-50%, -100%);
  padding: 4px 8px;
  border-radius: 4px;
  color: white;
  font-family: monospace;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 100;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: inherit;
    border-bottom-color: transparent;
    border-left-color: transparent;
    border-right-color: transparent;
  }
`;

export default MiniTimelineView;